import { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { UserInfoContext } from "../App";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import DateUtils from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/DateUtils";
import { Routes } from "../types/Routes";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import { WorklogReportSend } from "../models/WorklogReportSend";
import Assignation from "../models/Assignation";
import Holiday from "../models/Holiday";
import Position from "../models/Position";
import Timeoff from "../models/Timeoff";
import WorkLog from "../models/WorkLog";
import AssignationService from "../services/AssignationService";
import HolidayService from "../services/HolidayService";
import PositionService from "../services/PositionService";
import ProjectService from "../services/ProjectService";
import UserService from "../services/UserService";
import WorkLogService from "../services/WorkLogService";
import { WorlogWeeksDaysFilters } from "../utils/enums/filters/WorklogFilters";
import { TimeOffStatus } from "../utils/enums/TimeOffStatus";
import OpsDateUtils from "../utils/OpsDateUtils";
import OpsStringUtils from "../utils/OpsStringUtils";
import RoleUtils from "../utils/RoleUtils";
import TimeOffUtils from "../utils/TimeOffUtils";
import { Country } from "../utils/Countries";
import useUpdateUrl from "./useUpdateUrl";
import { useStoredDate } from "../hooks/useStoredDate";

const useWorkLogList = () => {
  const loggedUserInfo = useContext(UserInfoContext); // this represent the logged user
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [scrollToDay, setScrollToDay] = useState<number | undefined>(undefined);
  const [onlyWeekDays, setOnlyWeekDays] = useState<string>(WorlogWeeksDaysFilters.CALENDAR_WEEK);
  // User, active assignations and position information
  const [user, setUser] = useState<EnsolversOpsUser | undefined>(undefined); // this is the owner user of the worklogs
  const [userTimeoffs, setUserTimeoffs] = useState<Timeoff[]>();
  const [assignations, setAssignations] = useState<AutoCompleteUIDTO[]>([]);
  const [selectedAssignation, setSelectedAssignation] = useState<AutoCompleteUIDTO>(EMPTY_AUTOCOMPLETE);
  const [currPosition, setCurrPosition] = useState<Position | undefined>(undefined);
  const [assignation, setAssignation] = useState<Assignation | undefined>(undefined);
  // worklog entries related states
  const [results, setResults] = useState([] as WorkLog[]) // TODO type this results
  const [workLog, setWorkLog] = useState<WorkLog | undefined>(undefined);
  const [selectedWorkLog, setSelectedWorkLog] = useState<WorkLog | undefined>(undefined);
  const [isManagerOfProject, setIsManagerOfProject] = useState<boolean>(false);
  const [formDialog, setFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [update, setUpdate] = useState<number>(Date.now());
  const [fetchUpdate, setFetchUpdate] = useState<boolean>(false);
  // Reports data
  const [slackReportDialog, setSlackReportDialog] = useState<WorklogReportSend | undefined>(undefined);
  const [unified, setUnified] = useState<boolean>(false);
  // SelectedDate and holidays included in that month and year
  const [storedDate, setStoredDate] = useStoredDate();
  const [selectedDate, setSelectedDate] = useStoredDate();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  // URL Params and update function
  const searchParams = new URLSearchParams(useLocation().search);
  const queryYear = searchParams.get("year");
  const queryMonth = searchParams.get("month");
  const queryDay = searchParams.get("day");
  const userExternalId = searchParams.get("user");
  const paramAssignationExternalId = searchParams.get("assignation");
  const { updateUrl } = useUpdateUrl(Routes.WORKLOG, searchParams);
  const history = useHistory();

  const scrollToDayRef = useRef(scrollToDay);
  const isFirstComponentLoadRef = useRef(true);
  
  // On the first load of the component:
  // if minimum params are not present: replace URL with default params based on logged user
  // otherwise load the information based on the params present
  useEffect(() => {
    // This condition will ensure that the component will only load once and after the user information is loaded
    if (!isFirstComponentLoadRef.current || !loggedUserInfo.isUserLoaded()) {
      return;
    }
    isFirstComponentLoadRef.current = false;

    queryDay && setScrollToDay(parseInt(queryDay));
    if (!queryMonth || !queryYear || !userExternalId) {
      buildUrlParams();
    } else {
      (async () => {
        await loadUserData(userExternalId);
      })()
    }
  }, [loggedUserInfo]);

  useEffect(() => {
    scrollToDayRef.current = scrollToDay;

    return () => {
      scrollToDayRef.current = undefined;
    };
  }, [scrollToDay]);

  // After finishing loading the data, if there's a day param, wait 1sec and then scrolls to that day
  useEffect(() => {
    if (!isLoading && scrollToDay) {
      setTimeout(
        scrollToSelectedDay,
        1000
      )
    }
  }, [isLoading])

  // Once the Owner user of the worklogs is set, fetch its assignations and set the selected one. If no assignation param is present, select the first one available
  useEffect(() => {
    if (!!user) {
      (async () => {
        try {
          const assigs = await getAssignationsByUser(user.coreUserExternalId!, selectedDate)
          setAssignations(assigs);
          if (paramAssignationExternalId) {
            const matchingAssig = assigs.find(a =>
              a.externalId === paramAssignationExternalId
            )
            setSelectedAssignation(matchingAssig!);
          } else {
            setSelectedAssignation(assigs[0]);
          }
        } catch (error) {
          NotificationService.error("Error finding Assignation");
          setIsLoading(false);
        }
      })()
    }
  }, [user])

  // When the selectedAssignation changes, fetch the specific information for that Assignation and Position, including Timeoffs and Holidays
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSelectedAssignation() }, [selectedAssignation]);

  const loadSelectedAssignation = async () => {
    try {
      if (!selectedAssignation) {
        return;
      }
      if (selectedAssignation.externalId !== EMPTY_AUTOCOMPLETE.externalId && selectedAssignation.externalId !== paramAssignationExternalId) {
        buildUrlParams(selectedAssignation?.externalId);
      }
      if (loggedUserInfo.isUserLoaded() && userExternalId !== loggedUserInfo.user.externalId) {
        checkIfUserIsManagerFromProject();
      }
      if (!!selectedAssignation.externalId) {
        !isLoading && setIsLoading(true);
        
        let assignationResponse = await AssignationService.getByExternalId(selectedAssignation.externalId);
        let positionResponse = await PositionService.getByExternalId(assignationResponse.getContent().position.externalId!);

        setCurrPosition(positionResponse.getContent());
        setAssignation(assignationResponse.getContent());

        user && await getHolidays(user);

        TimeOffUtils.getUserTimeoffs(selectedAssignation.externalId, selectedDate).then((timeoffs) => {
          setUserTimeoffs(timeoffs);
        });
      }
    } catch (error) {
      NotificationService.error("Error getting position");
    } finally {
      setIsLoading(false);
    }
  }

  // Update the URL params
  /**
   * UPdate the URL and its params based on the selected information and update the data related
   * @param selectedAssignationExternalId 
   * @param selectedDate 
   */
  const buildUrlParams = (selectedAssignationExternalId?: string, selectedDate?: Date) => {
    if (!loggedUserInfo.isUserLoaded()) {
      return;
    }

    let queryDate = new Date(storedDate);
    if (selectedDate) {
      queryDate = new Date(selectedDate);
      setStoredDate(selectedDate);
    } else {
      queryYear && queryDate.setFullYear(parseInt(queryYear ?? storedDate.getFullYear().toString()));
      queryMonth ? queryDate.setMonth(parseInt(queryMonth) - 1) : queryDate.setMonth(storedDate.getMonth());
    }

    setSelectedDate(queryDate);
    
    // establish searchParams
    searchParams.set("year", queryDate.getFullYear().toString());
    searchParams.set("month", (queryDate.getMonth() + 1).toString());

    searchParams.set("user", userExternalId ?? loggedUserInfo.user.externalId)
    // establish assignation param
    if (selectedAssignationExternalId && selectedAssignationExternalId !== paramAssignationExternalId) {
      searchParams.set("assignation", selectedAssignationExternalId);
    } else {
      paramAssignationExternalId && searchParams.set("assignation", paramAssignationExternalId)
    }

    loadUserData(searchParams.get("user")!);

    updateUrl(Routes.WORKLOG, searchParams, );

    !fetchUpdate && setFetchUpdate(true);

    if (user && selectedDate) {
      getAssignationsByUser(user.coreUserExternalId!, selectedDate).then((res) => {
        if (paramAssignationExternalId) {
          const matchingAssig = res.find(a =>
            a.externalId === paramAssignationExternalId
          )
          setSelectedAssignation(matchingAssig!);
        } else {
          setSelectedAssignation(res ? res[0] : EMPTY_AUTOCOMPLETE);
        }
        setAssignations(res);
      });
    }
  }

  /**
   * If the month or year changes, update the selected assignation information
   * @param newDate Date object to compare with current {@link selectedDate}
   */
  const changeDate = async (newDate: Date | null) => {
    if (newDate && (newDate.getMonth() !== selectedDate.getMonth() || newDate?.getFullYear() !== selectedDate.getFullYear()) ) {
      buildUrlParams(selectedAssignation?.externalId, newDate);
    }
  }

  /**
   * Based on the Worklog's Owner and Country, get the matching holidays
   * @param user EnsolversOpsUser 
   */
  const getHolidays = async (user: EnsolversOpsUser) => {
    const worklogUser: EnsolversOpsUser = user;
    (
      await HolidayService.findHolidaysOfMonthByCountry(
        selectedDate.getMonth() + 1,
        worklogUser.country ? worklogUser.country : Country.ARGENTINA
      )
    )
      .onSuccess((response) => {
        response.getContent().forEach((holiday: Holiday) => {
          holiday.shortDate = OpsDateUtils.utcOffset(holiday.shortDate);
        });
        setHolidays(response.getContent());
      })
      .onError((response) => {
        NotificationService.error(response.getContent());
      });
  };

  /**
   * Get all the assignations that are active for the User and the date given
   * @param userExternalId COre User externalId
   * @param date Date object on which to find active Assignations ( Important information are Month nad Year )
   * @returns Active Assignations in format of {@link AutoCompleteUIDTO[]}
   */
  const getAssignationsByUser = async (userExternalId: string, date: Date): Promise<AutoCompleteUIDTO[]> => {
    const response = await AssignationService.findAssignationsNamesByUser(userExternalId, date);
    return response.getContent();
  };

  /**
   * Fetch the User information and update state
   * @param externalId Core User ExternalId
   */
  const loadUserData = async (externalId: string) => {
    try {

      if (externalId && (!user || externalId !== user.coreUserExternalId)) {
        !isLoading && setIsLoading(true);
        const response = await UserService.findByCoreUserId(externalId);
        const content = response.getContentOrThrowError();
        setUser(content);
      }
    } catch (error) {
      NotificationService.error("Error getting user information");
      setIsLoading(false);
    }
  };

  /**
   * If the logged user is not hte owner of the worklogs, verify if it's Admin or Manager of the Assignation's Project
   */
  const checkIfUserIsManagerFromProject = async () => {
    if (
      user?.coreUserExternalId !== loggedUserInfo.user.externalId && // if owner is not the logged user
      !RoleUtils.isAdmin() && // is not admin
      selectedAssignation && // has a selected assignation
      selectedAssignation.externalId !== EMPTY_AUTOCOMPLETE.externalId // with a non empty value 
    ) {
      const response = await ProjectService.isUserCoreManagerFromProjectByAssignation(
        selectedAssignation.externalId,
        loggedUserInfo.user.externalId
      );
      setIsManagerOfProject(response.succeeded());
    }
  };

  // Table functions
  const fetchWorklogData = async (pageNumber: number, count: number): Promise<PagedResult<WorkLog>> => {
    let result1 = results;

    if (!selectedAssignation) {
      return PagedResult.empty();
    }

    if (selectedAssignation.externalId === assignation?.externalId && paramAssignationExternalId) {

      // perform request
      if (queryMonth !== (selectedDate.getMonth() + 1).toString() ||
        queryYear !== selectedDate.getFullYear().toString() ||
        paramAssignationExternalId !== selectedAssignation.externalId ||
        userExternalId !== user?.coreUserExternalId ||
        results.length === 0 || fetchUpdate) {

        updateResultsData(pageNumber, count);
        
        setFetchUpdate(false);
      }
    }

    const filteredContent =
      onlyWeekDays === WorlogWeeksDaysFilters.WORKDAYS
        ? result1.filter((worklog: WorkLog) => !OpsDateUtils.isWeekend(OpsDateUtils.utcOffset(worklog.shortDay)))
        : result1;

    const newResult = new PagedResult(filteredContent, pageNumber, count);

    return newResult;
  };

  /**
   * Fetch the worklogs data
   * @param pageNumber Page number
   * @param count Amount of results per page
   */
  const updateResultsData = async (pageNumber: number, count: number) => {
    const result = await WorkLogService.getAllWithPrefilledEmptyPlaceholders(
      pageNumber,
      count,
      paramAssignationExternalId!,
      parseInt(queryYear!),
      parseInt(queryMonth!) - 1,
      userExternalId,
      user?.country ?? Country.ARGENTINA
    );

    const updatedWorklogs = result.content.map((worklog: WorkLog) => {
      return {
        ...worklog,
        shortDay: OpsDateUtils.utcOffset(worklog.shortDay),
      };
    });

    setResults(updatedWorklogs);
  }

  const handleOnDeleteWorkLog = async () => {
    if (!selectedWorkLog) return;

    try {
      await WorkLogService.delete(selectedWorkLog);

      setSelectedWorkLog(undefined);
      setUpdate(Date.now());
      setFetchUpdate(true);
    } catch (error) {
      NotificationService.error("Error trying to delete a worklog.");
    }
  };

  /**
   * If day param is prensent await until all the informatio nis loaded, then scroll to that day
   */
  const scrollToSelectedDay = () => {
    if (!scrollToDayRef.current || !scrollToDay) return;

    const dayElement = document.getElementById(`day-${scrollToDay}`);
    if (!dayElement) return;

    dayElement.scrollIntoView({ behavior: "smooth" });

    // Clean state
    scrollToDayRef.current = undefined;
    setScrollToDay(undefined);
  };

  const isAssignationActive = (instance: WorkLog) => {
    if (assignation?.shortEndDate) {
      const adjustedAssignationEndDate = new Date(assignation?.shortEndDate);
      adjustedAssignationEndDate.setDate(adjustedAssignationEndDate.getDate() + 1);

      if (!OpsDateUtils.isSameOrBefore(instance.shortDay, OpsDateUtils.makeLocalAppearUTC(adjustedAssignationEndDate)!)) {

        return false;
      }
    }

    if (
      assignation?.shortStartDate &&
      !OpsDateUtils.isSameOrAfter(instance.shortDay, OpsDateUtils.makeLocalAppearUTC(assignation.shortStartDate)!)
    ) {
      return false;
    }
    return true;
  };

  const handleOnClickRow = (instance: WorkLog) => {
    if (selectedAssignation === undefined) {
      NotificationService.error("First select an assignation");
      return;
    }

    if (!isAssignationActive(instance)) {
      NotificationService.error("Worklog out of assignation dates range");
      return;
    }

    if (!instance.externalId) {
      // New Instance Set Default Values
      instance.externalId = undefined;
      instance.hours = currPosition ? currPosition.externalHours : 8;
      instance.internalHours = currPosition ? currPosition.internalHours : 8;
      instance.assignationName = selectedAssignation.label;
      instance.assignationExternalId = selectedAssignation.externalId;

      handleTimeoffs(instance);
      setIsEditMode(false);
      setWorkLog(instance);
      setFormDialog(true);
    } else {
      // Edit Instance
      instance.assignationName = selectedAssignation.label;
      instance.assignationExternalId = selectedAssignation.externalId;

      setIsEditMode(true);
      setWorkLog(instance);
      setFormDialog(true);
    }
  };

  const handleTimeoffs = (instance: WorkLog) => {
    if (userTimeoffs) {
      userTimeoffs?.forEach((timeoff) => {
        if (OpsDateUtils.isBetweenOrEqual(timeoff.shortStartDate, timeoff.shortEndDate, instance.shortDay)) {
          let description: string;
          if (timeoff.status === TimeOffStatus.APPROVED) {
            description = "Timeoff: ".concat(
              OpsStringUtils.capitalize(timeoff.type),
              ", approved on ",
              DateUtils.formatAsDDMMMYYY(timeoff.approvedDate),
              ", by ",
              timeoff.approvedBy.firstName,
              " ",
              timeoff.approvedBy.lastName
            );
          } else {
            description = "Timeoff: ".concat(OpsStringUtils.capitalize(timeoff.type));
          }
          instance.description = userTimeoffs ? "Timeoff" : "";
          instance.description = userTimeoffs && timeoff.halfDay ? "" : userTimeoffs ? "Timeoff" : "";
          instance.internalDescription = userTimeoffs ? description : "";
          instance.hours = userTimeoffs ? 0 : currPosition ? currPosition.externalHours : 8;
          instance.hours = currPosition && timeoff.halfDay ? currPosition.externalHours / 2 : currPosition ? currPosition.externalHours : 8;
          instance.internalHours =
            currPosition && timeoff.halfDay ? currPosition.externalHours / 2 : currPosition ? currPosition.externalHours : 8;
        }
      });
    }
  };

  return {
    user,
    selectedAssignation,
    setSelectedAssignation,
    currPosition,
    workLog,
    setWorkLog,
    formDialog,
    setFormDialog,
    isEditMode,
    setIsEditMode,
    assignations,
    isLoading,
    update,
    selectedWorkLog,
    setSelectedWorkLog,
    unified,
    setUnified,
    slackReportDialog,
    setSlackReportDialog,
    holidays,
    userTimeoffs,
    selectedDate,
    setSelectedDate,
    isManagerOfProject,
    handleOnClickRow,
    isAssignationActive,
    handleOnDeleteWorkLog,
    fetchWorklogData,
    userExternalId,
    handleTimeoffs,
    onlyWeekDays,
    setOnlyWeekDays,
    queryYear,
    queryMonth,
    paramAssignationExternalId,
    getAssignationsByUser,
    changeDate,
    setFetchUpdate
  };
};

export default useWorkLogList;
